//
// Copyright (c) 2011-2024 by Curalate, Inc.
//
import { combineReducers } from 'redux';

import AccountSettingsReducer from '@account-settings/redux/accountSettings/accountSettingsSlice';
import AnalyticsProvidersReducer from '@account-settings/redux/analyticsProviders/analyticsProvidersSlice';
import DeviceSettingsReducer from '@account-settings/redux/deviceSettings/deviceSettingsReducer';
import EmailReportsReducer from '@account-settings/redux/emailReports/emailReportsSlice';
import FacebookInstagramSettingsReducer from '@account-settings/redux/facebook-instagram/facebookInstagramSettingsReducer';
import PinterestSettingsReducer from '@account-settings/redux/pinterest/pinterestSettingsSlice';
import AutomateRightsRequestReducer from '@account-settings/redux/rights-management/automateRightsRequestingSlice';
import TikTokSettingsReducer from '@account-settings/redux/tiktok/tiktokSettingsSlice';
import TrackingParamSettingsReducer from '@account-settings/redux/trackingParamSettings/trackingParamSettingsSlice';
import TwitterSettingsReducer from '@account-settings/redux/twitter/twitterSettingsSlice';
import { CreatorsCampaignMembers } from '@campaigns/redux/creatorsCampaignMembersSlice';
import { CreatorsCampaignPaymentMembers } from '@campaigns/redux/creatorsCampaignPaymentMembersSlice';
import CatalogSelectionReducer from '@catalog-ingestion/reducers/catalogSelectionReducer';
import ConnectPanelReducer from '@catalog-ingestion/reducers/connectPanelReducer';
import ExitConfirmationModalReducer from '@catalog-ingestion/reducers/exitConfirmationModalReducer';
import GoogleCatalogIngestionModalReducer from '@catalog-ingestion/reducers/googleCatalogIngestionModalReducer';
import ThresholdFailedConfirmationModalReducer from '@catalog-ingestion/reducers/thresholdFailedConfirmationModalReducer';
import CollectionsReducer from '@collections/redux/collectionsReducer';
import EmailBuilderCustomizationReducer from '@email-builder/reducers/emailBuilderCustomizationReducer';
import EmailBuilderDataReducer from '@email-builder/reducers/emailBuilderDataReducer';
import EmailBuilderPanelReducer from '@email-builder/reducers/emailBuilderPanelReducer';
import FacebookAudienceMetricsReducer from '@facebook/reducers/facebookAudienceMetricsReducer';
import FacebookEngagementMetricsReducer from '@facebook/reducers/facebookEngagementMetricsReducer';
import FacebookOnboardingReducer from '@facebook/reducers/facebookOnboardingReducer';
import FacebookPageReducer from '@facebook/reducers/facebookPageReducer';
import FacebookSummaryMetricsReducer from '@facebook/reducers/facebookSummaryMetricsReducer';
import GalleriesAutoApprovalModalReducer from '@galleries/reducers/autoApprovalModalSlice';
import GalleriesContentRightsModalReducer from '@galleries/reducers/contentRightsModalSlice';
import FullScreenWorkflowReducer from '@galleries/reducers/fullScreenWorkflowReducer';
import GalleriesActionBarReducer from '@galleries/reducers/galleriesActionBarReducer';
import galleriesAddToGalleriesModalReducer from '@galleries/reducers/galleriesAddToGalleriesModalReducer';
import GalleriesCreationReducer from '@galleries/reducers/galleriesCreationReducer';
import GalleriesCustomizationReducer from '@galleries/reducers/galleriesCustomizationReducer';
import GalleriesDataReducer from '@galleries/reducers/galleriesDataSlice';
import GalleriesDeleteGalleryReducer from '@galleries/reducers/galleriesDeleteGalleryReducer';
import GalleriesEmbedReducer from '@galleries/reducers/galleriesEmbedReducer';
import GalleriesMediaAutofillModalReducer from '@galleries/reducers/galleriesMediaAutofillModalReducer';
import GalleriesPickerReducer from '@galleries/reducers/galleriesPickerReducer';
import GalleriesUpdateGalleryNameReducer from '@galleries/reducers/galleriesUpdateGalleryNameReducer';
import GalleriesNoGalleriesModalReducer from '@galleries/reducers/noGalleriesModalReducer';
import GalleriesPinnedMediaToolbarReducer from '@galleries/reducers/pinnedMediaToolbarReducer';
import GalleriesPreviewGalleryModalReducer from '@galleries/reducers/previewGalleryModalReducer';
import GalleriesRequestRightsModalReducer from '@galleries/reducers/requestRightsModalReducer';
import GalleriesRequestRightsNonMentionedModalReducer from '@galleries/reducers/requestRightsNonMentionedModalReducer';
import GalleriesRightsOwnedOrPendingModalReducer from '@galleries/reducers/rightsOwnedOrPendingModalReducer';
import GalleriesRightsSkippedModalReducer from '@galleries/reducers/rightsSkippedModalReducer';
import UnmoderatedContentModalReducer from '@galleries/reducers/unmoderatedContentModalReducer';
import InstagramHashtagLimitsReducer from '@hashtag-limits/reducer/hashtagLimitsReducer';
import InfluencersListReducer from '@influencers/reducers/influencersListReducer';
import influencersSearchReducer from '@influencers/reducers/influencersSearchReducer';
import InsightsGalleriesReducer from '@insights/redux/galleries/insightsGalleriesSlice';
import InsightsLike2BuyReducer from '@insights/redux/like2buy/insightsLike2BuySlice';
import { reducer as InsightsLike2BuySummaryReducer } from '@insights/redux/like2buy/insightsLike2BuySummarySlice';
import { reducer as InsightsLike2BuyV2Reducer } from '@insights/redux/like2buy/insightsLike2BuyV2Slice';
import InsightsMetadataReducer from '@insights/redux/metadata/insightsMetadataSlice';
import InsightsRevealReducer from '@insights/redux/reveal/insightsRevealSlice';
import InsightsShowroomReducer from '@insights/redux/showroom/insightsShowroomSlice';
import InsightsSummaryReducer from '@insights/redux/summary/insightsSummarySlice';
import { ShopTagsMetricsReducer } from '@instagram-account/reducers/instagram-shop-tags-metrics-reducer';
import InstagramSummaryStatsReducer from '@instagram-account/reducers/instagram-summary-stats-reducer';
import InstagramUserStatsReducer from '@instagram-account/reducers/instagram-user-stats-reducer';
import InstagramAccountReducer from '@instagram-account/redux/account/instagramAccountSlice';
import InstagramAccountAudienceReducer from '@instagram-account/redux/audience/instagramAccountAudienceReducer';
import InstagramAudienceReducer from '@instagram-account/redux/audiencev2/instagramAudienceSlice';
import InstagramCompetitorsReducer from '@instagram-account/redux/competitors/instagramCompetitorsSlice';
import InstagramAccountEngagementPostsReducer from '@instagram-account/redux/engagement/posts/instagramAccountEngagementPostsReducer';
import InstagramAccountStoryGroupsEngagementReducer from '@instagram-account/redux/engagement/story-groups/instagramAccountStoryGroupsEngagementSlice';
import InstagramHashtagsReducer from '@instagram-account/redux/hashtags/instagramHashtagsReducer';
import InstagramHashtagPageReducer from '@instagram-account/redux/instagram-hashtag/hashtag-page/instagramHashtagPageSlice';
import InstagramHashtagInsightsReducer from '@instagram-account/redux/instagram-hashtag/insights/instagramHashtagInsightsSlice';
import InstagramHashtagRecentContentReducer from '@instagram-account/redux/instagram-hashtag/recent-content/instagramHashtagRecentContentSlice';
import InstagramShopsFeatureUgcReducer from '@instagram-account/redux/instagram-shops/feature-ugc/instagramShopsFeatureUgcSlice';
import InstagramShopsReducer from '@instagram-account/redux/instagram-shops/instagramShopsSlice';
import InstagramShopsProductSearchModalReducer from '@instagram-account/redux/instagram-shops/product-search-modal/instagramShopsProductSearchModalSlice';
import Like2BuyModerationReducer from '@instagram-account/redux/like2buy/like2buyModerationSlice';
import Like2BuyModerationModalReducer from '@instagram-account/redux/like2buy/modals/like2buyModalsSlice';
import InstagramMentionedAndTaggedReducer from '@instagram-account/redux/mentioned-and-tagged/InstagramMentionedAndTaggedSlice';
import InstagramSummaryReducer from '@instagram-account/redux/summary/instagramSummarySlice';
import InstagramPostUploaderReducer from '@instagram-post-uploader/reducer/instagramPostUploaderReducer';
import ToastNotificationsReducer from '@legacy-components/common/toast-notifications/toastNotificationsReducer';
import AllLabelsModalReducer from '@legacy-components/content/content-library-labels-modal/redux/allLabelsModalSlice';
import RequestRightsReducer from '@legacy-components/rights-management/request-rights-reducer';
import { UploadQueueReducer } from '@media-library/redux/upload-queue/uploadQueueSlice';
import PinterestAudienceReducer from '@pinterest/redux/audience/PinterestAudienceSlice';
import PinterestEngagementReducer from '@pinterest/redux/engagement/PinterestEngagementSlice';
import PinterestPostDetailsModalsReducer from '@pinterest/redux/post-details/modals/pinterestPostDetailsModalsSlice';
import PinterestPostDetailsReducer from '@pinterest/redux/post-details/pinterestPostDetailsSlice';
import PinterestScheduleReducer from '@pinterest/redux/schedule/pinterestScheduleSlice';
import PinterestSummaryReducer from '@pinterest/redux/summary/PinterestSummarySlice';
import PinterestUserReducer from '@pinterest/redux/user/pinterestUserSlice';
import { combineNestedReducers } from '@redux/combineNestedReducers';
import ContentDrawerReducer from '@redux/content-drawer/contentDrawerReducer';
import ScheduleMediaReducer from '@redux/schedule-media/scheduleMediaSlice';
import CampaignDetailsReducer from '@sampling/redux/campaign-details/campaignDetailsSlice';
import CampaignsListsReducer from '@sampling/redux/campaigns-lists/campaignsListsSlice';
import ShareActionBarReducer from '@share/reducers/shareActionBarReducer';
import ShareDataReducer from '@share/reducers/shareDataReducer';
import ShareOnboardingReducer from '@share/reducers/shareOnboardingReducer';
import StreamsDrawerReducer from '@streams-drawer/reducers/streams-drawer-reducer';
import TaggingFeedReducer from '@tagging/reducers/taggingFeedReducer';
import TaggingSettingsReducer from '@tagging/reducers/taggingSettingsReducer';
import TikTokAudienceReducer from '@tiktok/redux/audience/tiktokAudienceSlice';
import TikTokEngagementReducer from '@tiktok/redux/engagement/tiktokEngagementSlice';
import { reducer as TikTokLike2BuyCustomizationsReducer } from '@tiktok/redux/like2buy/customizations/tiktokLike2BuyCustomizationsSlice';
import { reducer as TikTokLike2BuyModalsReducer } from '@tiktok/redux/like2buy/modals/tiktokLike2BuyModalsSlice';
import { reducer as TikTokLike2BuyReducer } from '@tiktok/redux/like2buy/tiktokLike2BuySlice';
import TikTokSummaryReducer from '@tiktok/redux/summary/tiktokSummarySlice';
import TikTokUserReducer from '@tiktok/redux/user/tiktokUserSlice';
import TwitterAudienceReducer from '@twitterV2/redux/audience/twitterAudienceSlice';
import TwitterEngagementReducer from '@twitterV2/redux/engagement/twitterEngagementSlice';
import TwitterSummaryReducer from '@twitterV2/redux/summary/twitterSummarySlice';
import TwitterUserReducer from '@twitterV2/redux/user/twitterUserSlice';
import UIAModalReducer from '@uia/uia-modal-reducer';
import { UniversalCalendarReducer } from '@universal-scheduling/redux/calendar/universalCalendarSlice';
import { UniversalDraftsReducer } from '@universal-scheduling/redux/drafts/universalDraftsSlice';

import SyndicationInsightsReducer from 'app/analytics-at-a-glance/syndication-insights/redux-slice/syndicationInsightsSlice';
import BeautifyModalReducer from 'app/beautify-modal/redux/beautifyModalSlice';
import BulkActionsReducer from 'app/bulk-actions/reducers/bulk-actions-reducer';
import RoutingReducer from 'app/initialization/routing/routingReducer';
import SessionReducer from 'app/initialization/session/session-reducer';
import PromotedPostsAudienceReducer from 'app/promoted-posts/reducers/promoted-posts-audience-reducer';
import PromotedPostsBudgetReducer from 'app/promoted-posts/reducers/promoted-posts-budget-reducer';
import PromotedPostsReachEstimateReducer from 'app/promoted-posts/reducers/promoted-posts-reach-estimate-reducer';
import PromotedPostsReducer from 'app/promoted-posts/reducers/promoted-posts-reducer';
import PromotedPostsTargetReducer from 'app/promoted-posts/reducers/promoted-posts-target-reducer';
import PromotedPostsMetricsReducer from 'app/promoted-posts-metrics/reducers/promoted-posts-metrics-reducer';
import AssetFeedsModalReducer from 'app/streams/reducers/assetFeedsModalReducer';
import StreamCreationDataReducer from 'app/streams/reducers/streamCreationDataReducer';
import StreamDeletionDataReducer from 'app/streams/reducers/streamDeleteSlice';

export default {
  accountSettings: combineReducers({
    account: AccountSettingsReducer,
    analyticsProviders: AnalyticsProvidersReducer,
    devices: DeviceSettingsReducer,
    emailReports: EmailReportsReducer,
    socialMedia: combineReducers({
      pinterest: PinterestSettingsReducer,
      tiktok: TikTokSettingsReducer,
      twitter: TwitterSettingsReducer,
    }),
    rightsManagement: combineReducers({
      automateRightsRequests: AutomateRightsRequestReducer,
    }),
    trackingParams: TrackingParamSettingsReducer,
  }),

  beautifyModal: BeautifyModalReducer,

  bulkActions: BulkActionsReducer,

  campaigns: combineReducers({
    campaignDetails: CampaignDetailsReducer,
    campaignsLists: CampaignsListsReducer,
    creatorsCampaignMembers: CreatorsCampaignMembers,
    creatorsCampaignPaymentMembers: CreatorsCampaignPaymentMembers,
  }),

  catalogIngestionReducers: combineReducers({
    catalogSelection: CatalogSelectionReducer,
    connectPanel: ConnectPanelReducer,
    exitConfirmationModal: ExitConfirmationModalReducer,
    googleCatalogIngestionModal: GoogleCatalogIngestionModalReducer,
    thresholdFailedConfirmationModal: ThresholdFailedConfirmationModalReducer,
  }),

  collections: CollectionsReducer,

  contentDrawer: ContentDrawerReducer,

  emailBuilder: combineReducers({
    customization: EmailBuilderCustomizationReducer,
    data: EmailBuilderDataReducer,
    panel: EmailBuilderPanelReducer,
  }),

  facebook: combineReducers({
    audienceMetrics: FacebookAudienceMetricsReducer,
    engagementMetrics: FacebookEngagementMetricsReducer,
    onboarding: FacebookOnboardingReducer,
    pageDetails: FacebookPageReducer,
    summaryMetrics: FacebookSummaryMetricsReducer,
  }),

  facebookInstagramSettings: FacebookInstagramSettingsReducer,

  fullScreenWorkflows: combineReducers({
    fullscreenWorkflowMetadata: FullScreenWorkflowReducer,
  }),

  galleries: combineReducers({
    actionBar: GalleriesActionBarReducer,
    addToGalleriesModal: galleriesAddToGalleriesModalReducer,
    autoApprovalModal: GalleriesAutoApprovalModalReducer,
    contentRightsModal: GalleriesContentRightsModalReducer,
    creation: GalleriesCreationReducer,
    customization: GalleriesCustomizationReducer,
    data: GalleriesDataReducer,
    deleteGalleryModal: GalleriesDeleteGalleryReducer,
    embed: GalleriesEmbedReducer,
    mediaAutofillModal: GalleriesMediaAutofillModalReducer,
    noGalleriesModal: GalleriesNoGalleriesModalReducer,
    picker: GalleriesPickerReducer,
    pinnedMediaToolbar: GalleriesPinnedMediaToolbarReducer,
    previewGalleryModal: GalleriesPreviewGalleryModalReducer,
    requestRightsModal: GalleriesRequestRightsModalReducer,
    requestRightsNonMentionedModal:
      GalleriesRequestRightsNonMentionedModalReducer,
    rightsOwnedOrPendingModal: GalleriesRightsOwnedOrPendingModalReducer,
    rightsSkippedModal: GalleriesRightsSkippedModalReducer,
    unmoderatedContentModal: UnmoderatedContentModalReducer,
    updateGalleryName: GalleriesUpdateGalleryNameReducer,
  }),

  influencers: combineReducers({
    list: InfluencersListReducer,
    search: influencersSearchReducer,
  }),

  insights: combineReducers({
    galleries: InsightsGalleriesReducer,
    like2buy: InsightsLike2BuyReducer,
    like2BuySummary: InsightsLike2BuySummaryReducer,
    like2BuyV2: InsightsLike2BuyV2Reducer,
    metadata: InsightsMetadataReducer,
    reveal: InsightsRevealReducer,
    showroom: InsightsShowroomReducer,
    summary: InsightsSummaryReducer,
  }),

  /**
   * The new holding place for IG account redux code. This combined reducer should stay
   * in sync with the contents of the @instagram-account/redux directory.
   */
  instagram: combineReducers({
    account: InstagramAccountReducer,
    audience: InstagramAccountAudienceReducer,
    audienceV2: InstagramAudienceReducer,
    competitors: InstagramCompetitorsReducer,
    engagement: combineReducers({
      posts: InstagramAccountEngagementPostsReducer,
      storyGroups: InstagramAccountStoryGroupsEngagementReducer,
    }),
    hashtags: combineNestedReducers(InstagramHashtagsReducer, {
      insights: InstagramHashtagInsightsReducer,
      page: InstagramHashtagPageReducer,
      recentContent: InstagramHashtagRecentContentReducer,
    }),
    like2buy: combineNestedReducers(Like2BuyModerationReducer, {
      modals: Like2BuyModerationModalReducer,
    }),
    shops: combineNestedReducers(InstagramShopsReducer, {
      featureUgc: InstagramShopsFeatureUgcReducer,
      productSearchModal: InstagramShopsProductSearchModalReducer,
    }),
    mentionedAndTagged: InstagramMentionedAndTaggedReducer,
    summary: InstagramSummaryReducer,
  }),

  instagramAccountSummaryStats: InstagramSummaryStatsReducer,

  instagramHashtagLimits: InstagramHashtagLimitsReducer,

  instagramPostUploader: InstagramPostUploaderReducer,

  instagramUserStats: InstagramUserStatsReducer,

  notifications: ToastNotificationsReducer,

  mediaUploadQueue: UploadQueueReducer,

  pinterest: combineReducers({
    audience: PinterestAudienceReducer,
    engagement: PinterestEngagementReducer,
    postDetails: combineNestedReducers(PinterestPostDetailsReducer, {
      modals: PinterestPostDetailsModalsReducer,
    }),
    schedule: PinterestScheduleReducer,
    summary: PinterestSummaryReducer,
    user: PinterestUserReducer,
  }),

  promotedPosts: PromotedPostsReducer,
  promotedPostsAudience: PromotedPostsAudienceReducer,
  promotedPostsBudget: PromotedPostsBudgetReducer,
  promotedPostsMetrics: PromotedPostsMetricsReducer,
  promotedPostsReachEstimate: PromotedPostsReachEstimateReducer,
  promotedPostsTarget: PromotedPostsTargetReducer,

  requestRights: RequestRightsReducer,
  routing: RoutingReducer,

  scheduleMedia: ScheduleMediaReducer,

  session: SessionReducer,

  share: combineReducers({
    actionBar: ShareActionBarReducer,
    data: ShareDataReducer,
    onboarding: ShareOnboardingReducer,
    syndicationInsights: SyndicationInsightsReducer,
  }),

  shopTagsMetrics: ShopTagsMetricsReducer,

  streams: combineReducers({
    assetFeedsModal: AssetFeedsModalReducer,
    streamCreation: StreamCreationDataReducer,
    streamDeletion: StreamDeletionDataReducer,
    streamsDrawer: StreamsDrawerReducer,
  }),

  tagging: combineReducers({
    feedReducer: TaggingFeedReducer,
    settingsReducer: TaggingSettingsReducer,
  }),

  tiktok: combineReducers({
    audience: TikTokAudienceReducer,
    engagement: TikTokEngagementReducer,
    like2buy: combineNestedReducers(TikTokLike2BuyReducer, {
      customizations: TikTokLike2BuyCustomizationsReducer,
      modals: TikTokLike2BuyModalsReducer,
    }),
    summary: TikTokSummaryReducer,
    user: TikTokUserReducer,
  }),

  twitter: combineReducers({
    audience: TwitterAudienceReducer,
    engagement: TwitterEngagementReducer,
    summary: TwitterSummaryReducer,
    user: TwitterUserReducer,
  }),

  uiaModal: UIAModalReducer,

  universalSchedule: combineReducers({
    universalCalendar: UniversalCalendarReducer,
    universalDrafts: UniversalDraftsReducer,
  }),

  viewAllLabels: AllLabelsModalReducer,
};
